///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthPosition } from '../position';

import { MarkerStateEnum } from './marker-state-enum';
import { GtthMoveUnitCommand } from '../../engine/commands/node/move-unit-command';
import { GtthHistoryService } from '../../engine/history-service';
import {
    ActionsToolbarModeEnum,
    GtthStepGroupEnum,
    NodeTypeEnum,
    ProductTypeEnum,
    SectorPolarityEnum,
    TopologyAltitudeModeEnum
} from '../../../../generated/shared-types.model';
import { GtthNodeFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { TopologyLinkTypeEnum } from '../../../../services/project-socket/dto/topology-types.model';
import { GtthCreateLinkCommand } from '../../engine/commands/link/create-link-command';
import { _cloneDeep } from '../../../../shared/utils';
import { NodeFeatureView } from './view/node-feature-view';
import { LocationViewStateModel } from './view/location-view-state.model';

export class NodeFeature {
    historyService: GtthHistoryService;
    linkIds: Set<number>;
    protected readonly id: number;
    protected layerIndex: number;
    protected node_name: string;
    protected to_remove: boolean;
    protected height: number;
    protected alt_mode: TopologyAltitudeModeEnum;
    protected is_pop: boolean;
    protected is_enabled: boolean;
    protected mhType: NodeTypeEnum;
    protected blockedByPoleAngle: number;
    protected buDeviceAzimuth: number;
    protected buDevicePolarity: SectorPolarityEnum;
    protected predefinedBuProductId: number;
    protected position: GtthPosition;
    protected isNodeSelected = false;
    protected viewObject: NodeFeatureView;

    protected hasSector = false;

    private moveCommand: GtthMoveUnitCommand;
    private infoWindow: any;

    private currentMarkerState = MarkerStateEnum.DEFAULT;

    private isDraggable: boolean;

    private constructor(id: number, position: GtthPosition, historyService: GtthHistoryService) {
        this.id = id;
        this.historyService = historyService;
        this.position = position;
        this.linkIds = new Set();

        this.viewObject = new NodeFeatureView();
        this.to_remove = false;
        this.height = this.historyService.logicalTopology.getActiveLayerHeight();
        this.mhType = NodeTypeEnum.BEST_EFFORT;
    }

    static parseGeoJson(geoJson: GtthNodeFeatureDtoModel, historyService: GtthHistoryService): NodeFeature {
        const id = geoJson.getId();
        if (!id) {
            throw new Error('GtthNodeFeatureDtoModel without id!');
        }
        const terminalStation = new NodeFeature(id, GtthPosition.fromValues(geoJson.getLng(), geoJson.getLat()), historyService);
        terminalStation.parseGeoJson(geoJson);
        return terminalStation;
    }

    parseGeoJson(geoJson: GtthNodeFeatureDtoModel) {
        this.setName(geoJson.getName());
        this.to_remove = geoJson.getToRemove();
        this.height = geoJson.getHeight() !== undefined ? geoJson.getHeight() : this.historyService.logicalTopology.getActiveLayerHeight();
        this.alt_mode = geoJson.getAltMode() !== undefined ? geoJson.getAltMode() : TopologyAltitudeModeEnum.RELATIVE;
        this.is_pop = geoJson.getIsPop();
        this.is_enabled = geoJson.getIsEnabled();
        this.mhType = geoJson.getMhType();
        this.layerIndex = geoJson.getLayerIndex();
        this.blockedByPoleAngle = geoJson.getBlockedByPoleAngle();
        this.buDeviceAzimuth = geoJson.getBuDeviceAzimuth();
        this.buDevicePolarity = geoJson.getBuDevicePolarity();
        this.predefinedBuProductId = geoJson.getPredefinedBuProductId();
    }

    toGeoJson(): GtthNodeFeatureDtoModel {
        const terminalStationFeature = new GtthNodeFeatureDtoModel();
        terminalStationFeature.setId(this.getId());
        terminalStationFeature.setName(this.node_name);
        terminalStationFeature.setLat(this.position.latitude);
        terminalStationFeature.setLng(this.position.longitude);
        terminalStationFeature.setHeight(this.height);
        terminalStationFeature.setAltMode(this.alt_mode);
        terminalStationFeature.setMhType(this.getMhType());
        terminalStationFeature.setLayerIndex(this.getLayerIndex());
        terminalStationFeature.setBuDeviceAzimuth(this.getBuDeviceAzimuth());
        terminalStationFeature.setBuDevicePolarity(this.buDevicePolarity);
        terminalStationFeature.setPredefinedBuProductId(this.predefinedBuProductId);

        terminalStationFeature.setToRemove(this.to_remove);
        terminalStationFeature.setIsPop(this.is_pop);
        terminalStationFeature.setIsEnabled(this.is_enabled);
        terminalStationFeature.setBlockedByPoleAngle(this.blockedByPoleAngle);
        return terminalStationFeature;
    }

    getId(): number {
        return this.id;
    }

    getName(): string {
        return this.node_name;
    }

    getPosition(): GtthPosition {
        return _cloneDeep(this.position);
    }

    isPop(): boolean {
        return this.is_pop;
    }

    getHeight(): number {
        return this.height;
    }

    isEnabled(): boolean {
        return this.is_enabled;
    }

    getAltitudeMode(): TopologyAltitudeModeEnum {
        return this.alt_mode;
    }

    setToRemove(value: boolean) {
        this.to_remove = value;
    }

    public getMhType(): NodeTypeEnum {
        return this.mhType;
    }

    public setMhType(mhType: NodeTypeEnum) {
        this.mhType = mhType;
        this.refreshState();
    }

    public isMustBeConnected(): boolean {
        return this.isEnabled() && this.mhType !== NodeTypeEnum.OPTIONAL_BU;
    }

    public getLayerIndex(): number {
        return this.layerIndex;
    }

    public changeName(value: string) {
        this.historyService.interactiveWindowsService.nodeNameChanged(this.node_name, value);
        this.viewObject.changeTitle(value);
        this.node_name = value;
    }

    public setIsPop(value: boolean) {
        this.is_pop = value;
        this.refreshState();
    }

    public setLayerIndex(index: number): void {
        this.layerIndex = index;
        this.historyService.interactiveWindowsService.nodeLayerChanged(this.getName(), index);
        this.refreshState();

        this.linkIds.forEach((linkId) => this.historyService.logicalTopology.getLink(linkId).refreshColor());

        this.changeDraggable();
    }

    public setIsEnabled(value: boolean) {
        this.is_enabled = value;
        this.refreshState();

        this.historyService.interactiveWindowsService.nodeEnabled(this.getName(), this.is_enabled);
    }

    setAltitudeMode(value: TopologyAltitudeModeEnum) {
        this.alt_mode = value;
    }

    setHeight(value: number) {
        this.height = value;
    }

    changeDraggable() {
        this.isDraggable =
            this.historyService.isEditModeEnabled &&
            !this.historyService.logicalTopology.hasLinks(this.getId()) &&
            this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES &&
            this.isInActiveLayer();

        if (!this.isDraggable && this.viewObject.isViewDraggable()) {
            this.historyService.triggerNodeAbilityToDrag(undefined);
        }
    }

    move(newPosition: GtthPosition) {
        if (!this.position.equals(newPosition)) {
            this.position = _cloneDeep(newPosition);
            this.viewObject.move(this.position);
            if (this.infoWindow) {
                this.infoWindow.setPosition(newPosition);
            }
        }
    }

    draw(): void {
        this.viewObject.draw(this.historyService.map, this.position, this.getName(), this.getViewState());
    }

    hide() {
        this.viewObject.hide();
    }

    select() {
        if (!this.historyService.isEditModeEnabled) {
            return;
        }
        this.isNodeSelected = true;
        this.currentMarkerState = MarkerStateEnum.SELECTED;
        this.refreshState();

        this.historyService.interactiveWindowsService.nodeSelected(this.getName(), true);
    }

    unselect() {
        this.isNodeSelected = false;
        this.currentMarkerState = MarkerStateEnum.DEFAULT;
        this.refreshState();

        this.historyService.interactiveWindowsService.nodeSelected(this.getName(), false);
    }

    isSelected(): boolean {
        return this.isNodeSelected;
    }

    initListeners() {
        this.viewObject.addListener('dragstart', (mouseEvent: any) => {
            this.historyService.ngZone.run(() => {
                this.moveCommand = this.historyService
                    .command(GtthMoveUnitCommand)
                    .create(this.getId(), GtthPosition.fromEventObject(mouseEvent));
            });
        });

        this.viewObject.addListener('drag', (mouseEvent: any) => {
            this.historyService.ngZone.run(() => {
                if (this.moveCommand) {
                    this.moveCommand.updatePosition(GtthPosition.fromEventObject(mouseEvent));
                }
            });
        });

        this.viewObject.addListener('dragend', (mouseEvent: any) => {
            this.historyService.ngZone.run(() => {
                if (this.moveCommand) {
                    this.moveCommand.updatePosition(GtthPosition.fromEventObject(mouseEvent));
                    this.historyService.doCommand(this.moveCommand);
                }
            });
        });

        this.viewObject.addListener('click', () => {
            this.historyService.ngZone.run(() => {
                switch (this.historyService.toolbarMode) {
                    case ActionsToolbarModeEnum.NO_LOS:
                    case ActionsToolbarModeEnum.CONFIRMED_LOS:
                    case ActionsToolbarModeEnum.NOT_CONFIRMED_LOS:
                    case ActionsToolbarModeEnum.LINK:
                    case ActionsToolbarModeEnum.FIBER:
                        if (!this.historyService.logicalTopology.hasSelectedStations()) {
                            this.historyService.logicalTopology.addSelectedStation(this);
                        } else {
                            const fromStation = this.historyService.logicalTopology.getSelectedStations().next().value;
                            this.historyService.doCommand(
                                this.historyService
                                    .command(GtthCreateLinkCommand)
                                    .createFromIds(fromStation.getId(), this.getId(), this.historyService.toolbarMode)
                            );
                            this.historyService.logicalTopology.clearSelection();
                        }
                        break;
                    default:
                        if (!this.historyService.isCtrlKeyPressed) {
                            this.historyService.clearSelectionCloseWindowAndArea();
                        } else {
                            this.historyService.closeInfoWindows();
                        }
                        this.historyService.logicalTopology.addSelectedStation(this);
                }
            });
        });

        this.viewObject.addListener('rightclick', () => {
            this.historyService.ngZone.run(() => {
                this.showInfoWindow();
            });
        });

        this.viewObject.addListener('mouseover', () => {
            this.historyService.triggerNodeAbilityToDrag(this.isDraggable ? this.viewObject : undefined);
        });
    }

    destroyListeners() {
        this.viewObject.destroyListeners();
    }

    enableListeners() {
        this.isDraggable =
            this.historyService.isEditModeEnabled &&
            !this.historyService.logicalTopology.hasLinks(this.getId()) &&
            this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES;
    }

    disableListeners() {
        this.isDraggable = false;
        if (this.viewObject.isViewDraggable()) {
            this.historyService.triggerNodeAbilityToDrag(undefined);
        }
    }

    showInfoWindow() {
        this.historyService.infoWindowsService
            .openUnitInfoWindow(this.getId(), this.position)
            .then((infoWindow: google.maps.InfoWindow) => {
                this.infoWindow = infoWindow;
            });
    }

    refreshState(): void {
        this.viewObject.refreshMarker(this.getViewState());
    }

    hasFreezeLinks(): boolean {
        for (const linkId of this.linkIds) {
            if (this.historyService.logicalTopology.getLink(linkId).isUserForced()) {
                return true;
            }
        }

        return false;
    }

    public isInActiveLayer(): boolean {
        return (
            this.historyService.logicalTopology.activeLayerIdx === -1 ||
            this.historyService.logicalTopology.activeLayerIdx === this.layerIndex
        );
    }

    public getBlockedByPoleAngle(): number {
        return this.blockedByPoleAngle;
    }

    public setBlockedByPoleAngle(value: number): void {
        this.blockedByPoleAngle = value;
    }

    public getBuDeviceAzimuth(): number {
        return this.buDeviceAzimuth;
    }

    public setBuDeviceAzimuth(value: number): void {
        this.buDeviceAzimuth = value;
    }

    public getBuDevicePolarity(): SectorPolarityEnum {
        return this.buDevicePolarity;
    }

    public setBuDevicePolarity(value: SectorPolarityEnum): void {
        this.buDevicePolarity = value;
    }

    public getPredefinedBuProductId(): number {
        return this.predefinedBuProductId;
    }

    public setPredefinedBuProductId(value: number): void {
        this.predefinedBuProductId = value;
    }

    isHasSector(): boolean {
        return this.hasSector;
    }

    public checkHasSectors(): void {
        this.hasSector = false;

        for (const linkId of this.linkIds.values()) {
            const link = this.historyService.logicalTopology.getLink(linkId);
            if (!link.isActive() || link.getLinkType() === TopologyLinkTypeEnum.FIBER) {
                continue;
            }
            const productId = link.getNodeProduct(this.getId());
            const product = this.historyService.projectProductsService.getBaseRadioById(productId);

            if (product.isBU && this.historyService.projectProductsService.isDeviceHasSectors(productId)) {
                switch (product.productType) {
                    case ProductTypeEnum.TG_MH:
                        this.hasSector = true;
                        return;
                    case ProductTypeEnum.MH:
                        this.hasSector = this.hasSector || link.getSectorStationId() === this.getId();
                }
            }
        }
    }

    private setName(value: string) {
        this.viewObject.changeTitle(value);
        this.node_name = value;
    }

    private getViewState(): LocationViewStateModel {
        return {
            markerType: this.currentMarkerState,
            isHasSector: this.isHasSector(),
            isPop: this.isPop(),
            isEnable: this.isEnabled(),
            hasLinks: this.historyService.logicalTopology.hasLinksOrFibers(this.getId()),
            layerIndex: this.getLayerIndex(),
            isOptionalBu: this.getMhType() === NodeTypeEnum.OPTIONAL_BU,
            isInActiveLayer: this.isInActiveLayer()
        };
    }
}
